import logger from '@/utils/logger';
'use client'

import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { User, RealtimeChannel } from '@supabase/supabase-js';
import type { Profile } from '@/lib/supabase';
import { supabase, getProfile, startSessionHeartbeat, subscribeToChatSessions } from '@/lib/supabase';
import { getCachedProfile, cacheProfile, invalidateProfileCache } from '@/utils/profile-cache';
import { getResilientSession, invalidateSessionCache, updateSessionCache } from '@/lib/supabase-resilience';
import { DataManager } from '@/lib/data-manager';
import { RealtimeManager } from '@/lib/realtime-manager';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<any>;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signInWithGoogle: () => Promise<any>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // LOG IMMÉDIAT - Dès que le composant est appelé
  logger.warn('[AuthProvider] COMPONENT FUNCTION CALLED - AuthProvider is rendering!');
  
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const sessionHeartbeatRef = useRef<(() => void) | null>(null);
  const sessionSubscriptionRef = useRef<RealtimeChannel | null>(null);

  logger.log('[AuthProvider] State initialized:', { 
    user, 
    profile, 
    loading,
    timestamp: new Date().toISOString() 
  });

  useEffect(() => {
    logger.warn('[AuthProvider] useEffect STARTING - About to setup auth listener...');
    let isMounted = true;
    
    logger.log('[AuthProvider] Calling supabase.auth.onAuthStateChange()...');

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;

        logger.log('[AuthProvider] Auth state change:', event, session?.user?.id || 'undefined');

        // PROTECTION: Éviter les fluctuations de session
        // Ne pas traiter les changements temporaires vers undefined
        if (event === 'INITIAL_SESSION' && !session?.user && user) {
          logger.log('[AuthProvider] Session temporairement undefined - ignoré');
          return; // Ne pas déconnecter sur un changement temporaire
        }

        setUser(session?.user ?? null);

        // Mettre à jour le cache de résilience
        if (session) {
          updateSessionCache(session);
          logger.log('[AuthProvider] Session cache updated');
        }

        if (session?.user) {
          await loadProfile(session.user.id);

          // NOUVEAU: Init RealtimeManager (gère tous les subscriptions)
          RealtimeManager.init(session.user.id);
          logger.log('[AuthProvider] RealtimeManager initialized');

          // Heartbeat (optionnel, RealtimeManager gère déjà la connection)
          if (!sessionHeartbeatRef.current) {
            const stopHeartbeat = startSessionHeartbeat(session.user.id);
            sessionHeartbeatRef.current = stopHeartbeat;
          }

        } else if (event !== 'INITIAL_SESSION') {
          // Seulement arrêter si ce n'est pas un INITIAL_SESSION temporaire
          logger.log('[AuthProvider] Déconnexion réelle détectée');

          // Cleanup RealtimeManager
          RealtimeManager.destroy();
          logger.log('[AuthProvider] RealtimeManager destroyed');

          // Arrêter heartbeat
          if (sessionHeartbeatRef.current) {
            sessionHeartbeatRef.current();
            sessionHeartbeatRef.current = null;
          }

          setProfile(null);
          setLoading(false);
        }
      }
    );

    // ARCHITECTURE ASYNC-FIRST (comme OpenAI/ChatGPT)
    // Principe: NE JAMAIS bloquer l'UI en attendant Supabase
    // 1. Charger IMMÉDIATEMENT depuis localStorage (< 10ms)
    // 2. Valider en arrière-plan avec Supabase (non-bloquant)
    // 3. Update l'UI quand Supabase répond (progressive)
    
    logger.warn('[AuthProvider] ASYNC-FIRST session recovery (OpenAI-style)...');
    
    // ÉTAPE 1: Lecture IMMÉDIATE localStorage (synchrone, < 10ms)
    try {
      const stored = localStorage.getItem('sb-clairefy-auth');
      if (stored) {
        const data = JSON.parse(stored);
        
        if (data.user && data.access_token) {
          logger.log('[AuthProvider] INSTANT session restore from localStorage (< 10ms)');
          
          // Restaurer IMMÉDIATEMENT l'utilisateur
          setUser(data.user);
          
          // Charger le profile (avec cache, rapide)
          loadProfile(data.user.id);
          
          // Init RealtimeManager
          RealtimeManager.init(data.user.id);
          logger.log('[AuthProvider] RealtimeManager initialized (from localStorage)');
          
          // Démarrer heartbeat
          if (!sessionHeartbeatRef.current) {
            const stopHeartbeat = startSessionHeartbeat(data.user.id);
            sessionHeartbeatRef.current = stopHeartbeat;
          }
          
          // ÉTAPE 2: Validation Supabase en ARRIÈRE-PLAN (non-bloquant)
          logger.log('[AuthProvider] Background validation with Supabase...');
          
          getResilientSession(() => supabase.auth.getSession())
            .then((result) => {
              if (!isMounted) return;
              
              logger.log('[AuthProvider] Background validation complete:', {
                source: result.source,
                isDegraded: result.isDegraded,
              });
              
              // Si la session a changé, mettre à jour
              if (result.session?.user && result.session.user.id !== data.user.id) {
                logger.warn('[AuthProvider] Session changed during background validation');
                setUser(result.session.user);
                loadProfile(result.session.user.id);
              }
            })
            .catch((error) => {
              logger.warn('[AuthProvider] Background validation failed (non-blocking):', error);
              // Non-bloquant: l'utilisateur est déjà connecté avec localStorage
            });
          
          return; // On a déjà restauré la session, on sort
        }
      }
      
      // Pas de session en localStorage
      logger.log('[AuthProvider] No localStorage session, checking Supabase...');
    } catch (error) {
      logger.error('[AuthProvider] Error reading localStorage:', error);
    }
    
    // ÉTAPE 3: Si pas de localStorage, on doit attendre Supabase (1ère connexion)
    // Mais on met un timeout court de 3s pour ne pas bloquer
    logger.log('[AuthProvider] First-time session check with 3s timeout...');
    
    const quickTimeout = new Promise<null>((resolve) => {
      setTimeout(() => {
        logger.warn('[AuthProvider] Quick timeout (3s), assuming no session');
        resolve(null);
      }, 3000);
    });
    
    Promise.race([
      getResilientSession(() => supabase.auth.getSession()),
      quickTimeout,
    ])
      .then((result) => {
        if (!isMounted) return;
        
        if (!result) {
          // Timeout: pas de session
          logger.log('[AuthProvider] No session found (timeout), setting loading=false');
          setLoading(false);
          return;
        }
        
        if (result.session?.user) {
          logger.log('[AuthProvider] Session found:', result.session.user.id);
          setUser(result.session.user);
          loadProfile(result.session.user.id);
          
          if (!sessionHeartbeatRef.current) {
            const stopHeartbeat = startSessionHeartbeat(result.session.user.id);
            sessionHeartbeatRef.current = stopHeartbeat;
          }
        } else {
          logger.log('[AuthProvider] No session found, setting loading=false');
          setLoading(false);
        }
      })
      .catch((error) => {
        logger.error('[AuthProvider] Session check failed:', error);
        setLoading(false);
      });

    return () => {
      isMounted = false;
      subscription.unsubscribe();

      // Nettoyer heartbeat et subscriptions
      if (sessionHeartbeatRef.current) {
        sessionHeartbeatRef.current();
        sessionHeartbeatRef.current = null;
      }
      if (sessionSubscriptionRef.current) {
        sessionSubscriptionRef.current.unsubscribe();
        sessionSubscriptionRef.current = null;
      }
    };
  }, []); // Remove initialized dependency

  const loadProfile = useCallback(async (userId: string) => {
    logger.warn('[loadProfile] Using DataManager (cache-first)');
    
    try {
      // DataManager gère TOUT : Memory cache → IndexedDB → Supabase
      // TIMEOUT: 7s max (DataManager a 5s, on ajoute 2s de marge)
      const timeoutPromise = new Promise<null>((resolve) => 
        setTimeout(() => {
          logger.warn('[loadProfile] Timeout after 7s, continuing without profile');
          resolve(null);
        }, 7000)
      );

      const profile = await Promise.race([
        DataManager.getProfile(userId),
        timeoutPromise
      ]);
      
      if (profile) {
        logger.log('[loadProfile] Profile loaded from DataManager');
        setProfile(profile);
      } else {
        logger.warn('[loadProfile] No profile available (timeout or not found) - UI will continue');
        setProfile(null);
      }
    } catch (error) {
      logger.error('[loadProfile] Error:', error);
      setProfile(null);
    } finally {
      // TOUJOURS débloquer, même en cas d'erreur/timeout
      setLoading(false);
      logger.log('[loadProfile] Loading complete (setLoading(false) called)');
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { data, error };
  }, []);

  const signUp = useCallback(async (email: string, password: string, metadata?: any) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    });
    return { data, error };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: 'https://clairify.fr/studio',
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        }
      }
    });
    return { data, error };
  }, []);

  const signOut = async () => {
    try {
      logger.log('[AuthProvider] Signing out...');
      
      // Clear local state first
      setUser(null);
      setProfile(null);
      
      // Cleanup DataManager (cache)
      await DataManager.clearAll();
      logger.log('[AuthProvider] DataManager cleared');
      
      // Cleanup RealtimeManager (subscriptions)
      RealtimeManager.destroy();
      logger.log('[AuthProvider] RealtimeManager destroyed');
      
      // Sign out from Supabase
      await supabase.auth.signOut();
      
      // Invalider le cache de résilience
      invalidateSessionCache();
      logger.log('[AuthProvider] Resilience cache cleared');
      
      // SÉCURITÉ #28: Clear sélectif (auth only, préserver préférences user)
      // NE PAS utiliser localStorage.clear() qui efface cookie_consent, theme, etc.
      const authKeys = Object.keys(localStorage).filter(k => 
        k.startsWith('sb-') || // Supabase auth tokens
        k.includes('auth') ||   // Auth-related keys
        k.includes('session') || // Session data
        k.includes('supabase')  // Supabase data
      );
      authKeys.forEach(k => localStorage.removeItem(k));
      logger.log('[AuthProvider] Auth keys cleared from localStorage');
      
      // OPTIMISATION: Invalider le cache profil
      invalidateProfileCache();
      logger.log('[AuthProvider] Profile cache cleared');
      
      // Clear session storage (moins critique mais par sécurité)
      sessionStorage.clear();
      
      logger.log('[AuthProvider] Sign out complete');
    } catch (error) {
      logger.error('[AuthProvider] Error signing out:', error);
      throw error;
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await loadProfile(user.id);
    }
  };

  const value = useMemo(() => {
    logger.log('[AuthProvider] useMemo recalculating context value:', {
      hasUser: !!user,
      hasProfile: !!profile,
      loading,
      timestamp: new Date().toISOString(),
    });
    
    return {
      user,
      profile,
      loading,
      signIn,
      signUp,
      signInWithGoogle,
      signOut,
      refreshProfile
    };
  }, [user, profile, loading, signIn, signUp, signInWithGoogle, refreshProfile]);

  // Debug: Log every render with state
  logger.log('[AuthProvider] Rendering Provider with:', { 
    hasUser: !!user, 
    hasProfile: !!profile, 
    loading 
  });
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}